  <template>
    <div class="block ties-header">
      <h4 class="title is-4" v-if="!hasTies">
        We haven't found any collaborations at this university. <br>
        Do you know of one? <a :href="`mailto:${this.contact.mail}`">Get in touch!</a>
      </h4>

      <h4 class="title is-4" v-else>
        <span id="university_name">{{ academy.name }}</span>
        collaborates on <span class="countValue"> {{ this.numberActive[1] }} </span> active projects
        with <span class="countValue">{{ this.numberActive[0] }} </span> Israeli academic institutions.
      </h4>

      <p v-if="academy?.display_description">
        {{ academy.description }}
      </p>
    </div> <!-- .ties-header -->

    <article class="message is-small is-info">
      <div class="message-body">
        We try to keep this overview up to date and accurate.
        The EC Cordis data was retrieved <strong>17.03.2025</strong> and the other
        data is accurate to our best knowledge as of <strong>06.04.2025</strong>. If you have
        any new information or corrections, please
        <a :href="`mailto:${this.contact.mail}`">contact us</a>.
      </div>
    </article>

    <div class="block ties-list">
      <div v-for="(instituteTies, key) in blurb.ties" :key="key" class="box">
        <h4 class="title is-4">{{ key }}</h4>
        <p class="is-6 psubtitle" v-if="partners.hasOwnProperty(key)" v-html="partners[key]">
        </p>

        <!-- Active ties -->
        <div class="mt-4" v-for="tie in instituteTies.filter(t => isOngoing(t))" :key="tie.description">

          <p><span class="tag is-warning is-light">Active</span>
            <span class="pcont" v-if="tie.kind === 'Horizon 2025'">
              <span class="pname" v-html="tie.project_name"></span>
              (<span class="ptitle" v-html="tie.project_title"></span>),
              European project together with
              <span v-html="key"></span>.
              <span class="psource"><a :href="'https://cordis.europa.eu/project/id/' + tie.url">Source</a></span>
            </span>
            <span class="pcont" v-if="tie.kind === 'Bilateral'">
              <span class="pname" v-html="tie.project_name"></span>
              (<span class="ptitle" v-html="tie.project_title"></span>).
              <span class="psource"><a :href="tie.url">Source</a></span>
            </span>
          </p>

          <div class="rangeCont" >
            <span class="range"><span v-html="tie.start_date"></span> &mdash; <span v-html="tie.end_date"></span></span>
          </div>
        </div>

        <!-- Start: Ended ties -->
        <div v-if="instituteTies.filter(t => !isOngoing(t)).length > 0">
          <div>
            <button @click="toggleEndedTies(key)" class="toggle-button">
              {{ expandedTies[key] ? 'Hide' : 'Show' }} ended ties ({{
                instituteTies.filter(t => !isOngoing(t)).length
              }})
            </button>
          </div>
        </div>

        <transition name="fade">
          <div v-if="expandedTies[key]">
            <div v-for="tie in instituteTies.filter(t => !isOngoing(t))"
                 :key="tie.description"
                 class="tieText">
              <div class="inactive">
                <p v-html="tie.description"></p>
                <p class="end-date">Ended: {{ tie.end_date }}</p>
              </div>
              <ul v-if="tie.URLs">
                <li v-for="URL in tie.URLs" :key="URL">
                  <a :href="URL" target="_blank">Source</a>
                </li>
              </ul>
            </div>
          </div>
        </transition>

      </div>
    </div> <!-- .ties-list -->

    <div class="campaigning-groups block mt-6" v-if="academy.groups.length > 0">
      <h4 class="title is-4">Groups campaigning for Palestine</h4>
      <div v-for="item in academy.groups" :key="item.name" class="tieItem">
        <div class="card mt-4">
          <div class="card-content">
            <div class="media">
              <div class="media-left">
                <figure class="image is-48x48">
                  <img
                      :src="require('@/country_specific/assets/logo_thumbs/' + item.img)"
                      class="icon"
                      @error="$event.target.style.display='none'"
                  />
                </figure>
              </div>
              <div class="media-content">
                <p class="title is-4">{{ item.name }}</p>
                <p class="subtitle is-6"><a :href="item.link" target="_blank">{{ item.link }}</a></p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div> <!-- .campaigning-groups -->
  </template>

  <script>
  import partners from "@/country_specific/assets/references/partners.json";
  import ties from '@/country_specific/assets/references/ties.json';
  import contact from '@/country_specific/assets/references/countrySpecificValues.json'
  import {ref} from 'vue'

  export default {
    name: "TiesList",
    components: {
    },
    props: {
      academy: {
        type: Object,
      },
    },
    data() {
      return {
        width: document.documentElement.clientWidth,
        partners: partners,
        partnerTriggerValue: [],
        contact: contact,
        showEndedTies: false
      };
    },
    mounted() {
      window.addEventListener("resize", this.changeClass);
      for (var key in partners) {
        this.partnerTriggerValue[key] = false;
      }
    },
    computed: {
      blurb() {
        if (this.academy.tag in ties){
          return ties[this.academy.tag];
        }
        else {
          return {};
        }
      },
      classVariable() {
        if (this.width < 610) {
          return "smallBlurb";
        } else {
          return "largeBlurb";
        }
      },
      legend() {
        if (this.width < 610) {
          return "legendSmall";
        } else {
          return "legendLarge";
        }
      },

      hasTies() {
        const ties = this.blurb.ties || {};
        return Object.entries(ties).length > 0;
      },

      endedTies() {
        return this.blurb.ties.filter(t => !this.isOngoing(t));
      },

      activeTies() {
        return this.blurb.ties.filter(t => this.isOngoing(t));
      },

      numberActive() {

        const today = new Date()
        // If no end_date is specified, the project is considered as current
        const isOngoing = (project) => project?.end_date ? new Date(project.end_date) > today : true

        let tmp = this.blurb.ties;
        let active_projects = 0
        let active_partners = 0
        let partner_active = 0
        Object.keys(tmp).forEach(function (key) {
          partner_active = 0
          for (let i = 0; i < (tmp[key].length); i++) {
            if (isOngoing(tmp[key][i])) {
              active_projects++
              partner_active = 1
            }
          }
          if (partner_active) {
            active_partners++
          }
        });

        return [active_partners, active_projects]
      }
    },
    methods: {
      changeClass() {
        this.width = document.documentElement.clientWidth;
      },
      TogglePopup(key) {
        this.partnerTriggerValue[key] = !this.partnerTriggerValue[key];
      },
      isOngoing(project) {
        const today = new Date()
        // If no end_date is specified, the project is considered as current
        return project?.end_date ? new Date(project.end_date) > today : true
      }
    },
    setup() {

      const expandedTies = ref({})
      const toggleEndedTies = (partnerId) => {
        expandedTies.value[partnerId] = !expandedTies.value[partnerId]
      }

      return {
        expandedTies,
        toggleEndedTies
      }
    },
  };
  </script>

  <!-- Add "scoped" attribute to limit CSS to this component only -->
  <style scoped>
  .box, .card {
    border: 1px solid rgba(0, 0, 0, 0.1);
    box-shadow: 0 0.3em 0.5em -0.225em hsla(var(--bulma-shadow-h), var(--bulma-shadow-s), var(--bulma-shadow-l), 0.1), 0 0px 0 1px hsla(var(--bulma-shadow-h), var(--bulma-shadow-s), var(--bulma-shadow-l), 0.02);
  }

  ul {
    display: flex;
    flex-wrap: wrap;
  }

  li::after {
    content: ",";
    margin-right: 5px;
  }

  li:last-child::after {
    content: none;
    margin: 0;
  }

  .icon {
    width: 56px;
    height: 56px;
    margin-left: 7px;
    margin-right: 7px;
    padding-top: 2px;
    padding-bottom: 2px;
    margin-top: auto;
    margin-bottom: auto;
  }

  .iconMiddle {
    width: 60px;
    height: 60px;
    margin-left: auto;
    margin-right: auto;
    margin-top: auto;
    margin-bottom: auto;
  }

  .iconLegend {
    width: 40px;
    height: 40px;
    margin-left: 10px;
    margin-right: 10px;
    margin-top: auto;
    margin-bottom: auto;
  }

  .iconsTogether {
    width: 140px;
  }

  .subsection {
    
    display: flex;
  }


  .subsectionLegend {
    
    /*display: flex;*/
    border: grey 1px solid;
    padding: 10px;
  }

  .legendItem {
    display: flex;
  }

  .subsectiondescription {
    padding-left: 10px;
    align-items: center;
  }

  .parentContainer {
    display: flex;
    flex-flow: row wrap;
  }

  .legendLarge {
    width: 36%;

    padding: 2%;
    float: left;
    left: 20vw;
    /*padding: 10px
  /*flex-grow: 4*/
  }

  .legendSmall {
    padding: 2%;
    /*padding: 10px
  /*flex-grow: 4*/
    margin-left: auto;
    margin-right: auto;
  }


  .tieText {
    border-left: 4px solid var(--c9);
    padding-left: 16px;
    margin: 12px 0;
  }

  .toggle-button {
    border: 1px solid var(--c9);
    background: var(--c9);
    color: white;
    padding: 4px 12px;
    font-size: 0.9em;
    cursor: pointer;
    border-radius: 4px;
    transition: all 0.2s ease;
  }

  .toggle-button:active {
    transform: translateY(1px);
  }

  .pname {
    font-weight: bold;
  }

  .ptitle {
    font-style: italic;
  }

  .psubtitle {
    font-weight: 600;
    color: rgb(92, 92, 92);
  }


  .psource {
    margin-left: auto; float: right;
  }

  .rangeCont {
    text-align: right;
  }
  .range {
    color: gray;
  }


  </style>
